import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getAdminToken } from '../components/AdminAuthGate';

axios.defaults.withCredentials = true;

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function emptySection() {
  return { title: '', body: '' };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('read_failed'));
    r.readAsDataURL(file);
  });
}

function msgLooksOk(text) {
  if (!text) return false;
  if (text === '已儲存') return true;
  if (text.startsWith('圖片已插入')) return true;
  return false;
}

export default function MemberBenefitsAdmin() {
  const [pageTitleZh, setPageTitleZh] = useState('');
  const [pageTitleEn, setPageTitleEn] = useState('');
  const [sectionsZh, setSectionsZh] = useState([emptySection()]);
  const [sectionsEn, setSectionsEn] = useState([emptySection()]);
  const [tab, setTab] = useState('zh');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileInputRefs = useRef({});

  const load = useCallback(async () => {
    setMsg('');
    const { data } = await axios.get('/api/admin/member-benefits', { headers: authHeaders() });
    if (!data?.ok) throw new Error('load_failed');
    setPageTitleZh(String(data.pageTitle?.zh ?? ''));
    setPageTitleEn(String(data.pageTitle?.en ?? ''));
    const zh = Array.isArray(data.sections?.zh) ? data.sections.zh : [];
    const en = Array.isArray(data.sections?.en) ? data.sections.en : [];
    setSectionsZh(zh.length ? zh.map((r) => ({ title: r.title || '', body: r.body || '' })) : []);
    setSectionsEn(en.length ? en.map((r) => ({ title: r.title || '', body: r.body || '' })) : []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await load();
      } catch (e) {
        setMsg(e?.response?.data?.error || e?.message || '載入失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const currentSections = tab === 'zh' ? sectionsZh : sectionsEn;
  const setCurrentSections = tab === 'zh' ? setSectionsZh : setSectionsEn;

  const updateSection = (idx, field, value) => {
    setCurrentSections((rows) => {
      const next = rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
      return next;
    });
  };

  const addSection = () => {
    setCurrentSections((rows) => [...rows, emptySection()]);
  };

  const removeSection = (idx) => {
    setCurrentSections((rows) => rows.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    setCurrentSections((rows) => {
      const j = idx + dir;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleImageUpload = async (idx, e) => {
    const file = e?.target?.files?.[0];
    if (e?.target) e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMsg('請選擇圖片檔');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMsg('圖片不可超過 10MB');
      return;
    }
    const upKey = `${tab}-${idx}`;
    setUploadingKey(upKey);
    setMsg('');
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { data } = await axios.post(
        '/api/admin/member-benefits/upload-image',
        { file: dataUrl },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      if (!data?.ok || !data.imageUrl) throw new Error(data?.error || 'upload_failed');
      const snippet = `\n\n![圖片](${data.imageUrl})\n\n`;
      if (tab === 'zh') {
        setSectionsZh((rows) =>
          rows.map((r, i) => (i === idx ? { ...r, body: (r.body || '') + snippet } : r)),
        );
      } else {
        setSectionsEn((rows) =>
          rows.map((r, i) => (i === idx ? { ...r, body: (r.body || '') + snippet } : r)),
        );
      }
      setMsg('圖片已插入此段內文末尾（可再剪貼調整）');
    } catch (err) {
      setMsg(err?.response?.data?.error || err.message || '上傳失敗');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(
        '/api/admin/member-benefits',
        {
          pageTitle: { zh: pageTitleZh, en: pageTitleEn },
          sections: {
            zh: sectionsZh.map((s) => ({ title: s.title, body: s.body })),
            en: sectionsEn.map((s) => ({ title: s.title, body: s.body })),
          },
        },
        { headers: authHeaders() },
      );
      setMsg('已儲存');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = (active) => ({
    padding: '8px 16px',
    fontWeight: 800,
    border: '1px solid #ccc',
    borderRadius: 8,
    cursor: 'pointer',
    background: active ? '#007bff' : '#fff',
    color: active ? '#fff' : '#111',
  });

  return (
    <div
      style={{
        padding: 16,
        paddingBottom: 96,
        maxWidth: 960,
        margin: '0 auto',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>會員權益內容</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>
        設定頁面大標與多段段落。內文支援 Markdown；每段可用「上傳圖片插入」或自行貼圖片網址。前台：<code>/member-benefits</code>
      </p>

      {msg ? (
        <div
          style={{
            marginBottom: 12,
            color: msgLooksOk(msg) ? '#2e7d32' : '#c62828',
            fontWeight: 700,
          }}
        >
          {msg}
        </div>
      ) : null}

      <div style={{ marginBottom: 16, display: 'grid', gap: 10 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontWeight: 700 }}>頁面大標（中文）</span>
          <input
            value={pageTitleZh}
            onChange={(e) => setPageTitleZh(e.target.value)}
            style={{ padding: 10, fontSize: 15, borderRadius: 8, border: '1px solid #ccc' }}
            placeholder="例如：齊文會員福利"
          />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontWeight: 700 }}>頁面大標（English）</span>
          <input
            value={pageTitleEn}
            onChange={(e) => setPageTitleEn(e.target.value)}
            style={{ padding: 10, fontSize: 15, borderRadius: 8, border: '1px solid #ccc' }}
            placeholder="e.g. Singularity Member Benefits"
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 800 }}>編輯語系：</span>
        <button type="button" style={tabBtn(tab === 'zh')} onClick={() => setTab('zh')}>
          中文段落
        </button>
        <button type="button" style={tabBtn(tab === 'en')} onClick={() => setTab('en')}>
          English
        </button>
      </div>

      {loading ? (
        <div>載入中…</div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {currentSections.length === 0 ? (
            <div style={{ color: '#666', fontWeight: 600 }}>（此語系尚無段落，請按下方新增）</div>
          ) : null}
          {currentSections.map((row, idx) => (
            <div
              key={`${tab}-${idx}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: 10,
                padding: 12,
                background: '#fafafa',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 800 }}>段落 {idx + 1}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    上移
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === currentSections.length - 1}>
                    下移
                  </button>
                  <button type="button" onClick={() => removeSection(idx)}>
                    刪除
                  </button>
                </div>
              </div>
              <label style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>標題</span>
                <input
                  value={row.title}
                  onChange={(e) => updateSection(idx, 'title', e.target.value)}
                  style={{ padding: 8, fontSize: 15, borderRadius: 6, border: '1px solid #ccc' }}
                />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>內文（Markdown）</span>
                <textarea
                  value={row.body}
                  onChange={(e) => updateSection(idx, 'body', e.target.value)}
                  rows={10}
                  placeholder={'段落文字…\n\n也可按下方上傳圖片，或手動：\n![示意](https://…)\n\n- 項目'}
                  style={{ padding: 8, fontSize: 14, borderRadius: 6, border: '1px solid #ccc', fontFamily: 'inherit' }}
                />
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  ref={(el) => {
                    const k = `${tab}-${idx}`;
                    if (el) fileInputRefs.current[k] = el;
                    else delete fileInputRefs.current[k];
                  }}
                  style={{ display: 'none' }}
                  onChange={(e) => void handleImageUpload(idx, e)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[`${tab}-${idx}`]?.click()}
                  disabled={saving || uploadingKey === `${tab}-${idx}`}
                  style={{
                    padding: '8px 14px',
                    fontWeight: 700,
                    borderRadius: 8,
                    border: '1px solid #888',
                    background: '#fff',
                    cursor: saving || uploadingKey === `${tab}-${idx}` ? 'wait' : 'pointer',
                  }}
                >
                  {uploadingKey === `${tab}-${idx}` ? '上傳中…' : '上傳圖片插入'}
                </button>
                <span style={{ fontSize: 13, color: '#666' }}>插入於此段內文末尾</span>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addSection}
            style={{
              padding: '10px 14px',
              fontWeight: 800,
              borderRadius: 8,
              border: '1px dashed #888',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            + 新增段落
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving || loading}
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 9999,
          padding: '12px 22px',
          fontSize: 16,
          fontWeight: 900,
          background: saving ? '#374151' : '#111',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          cursor: saving ? 'wait' : 'pointer',
          boxShadow: '0 10px 26px rgba(0,0,0,.25)',
          opacity: saving || loading ? 0.75 : 1,
        }}
      >
        {saving ? '儲存中…' : '儲存'}
      </button>
    </div>
  );
}
